import useSWR from "swr";
import { fetcher } from "@/lib/api";
import { PageHeader, Section } from "@/components/Heading";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell } from "recharts";

const TONES = ["hsl(var(--primary))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

function PieBlock({ title, eyebrow, data }) {
  return (
    <Section eyebrow={eyebrow} title={title}>
      <div className="hairline p-4">
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} stroke="hsl(var(--background))" strokeWidth={3}>
              {data.map((_, i) => <Cell key={i} fill={TONES[i % TONES.length]} />)}
            </Pie>
            <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))" }} />
          </PieChart>
        </ResponsiveContainer>
        <div className="flex justify-around flex-wrap mt-2">
          {data.map((t, i) => (
            <div key={t.name} className="text-center px-2">
              <div className="w-3 h-3 mx-auto" style={{ background: TONES[i % TONES.length] }} />
              <div className="label-caps mt-1">{t.name}</div>
              <div className="font-mono text-sm">{t.value}</div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

function BarBlock({ title, eyebrow, data, xKey, yKey }) {
  return (
    <Section eyebrow={eyebrow} title={title}>
      <div className="hairline overflow-hidden">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data} margin={{ top: 12, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" />
            <XAxis dataKey={xKey} stroke="hsl(var(--muted-foreground))" fontSize={10} />
            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
            <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
            <Bar dataKey={yKey} fill="hsl(var(--primary))" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Section>
  );
}

export default function HrDemographics() {
  const { data } = useSWR("/hr/demographics", fetcher);
  if (!data) return null;

  const total = (data.gender || []).reduce((s, g) => s + g.value, 0);
  const male = (data.gender || []).find((g) => g.name === "M")?.value || 0;
  const female = (data.gender || []).find((g) => g.name === "F")?.value || 0;
  const malePct = total ? Math.round((male / total) * 100) : 0;
  const youngBucket = (data.age_buckets || []).reduce((m, b) => (b.count > (m?.count || 0) ? b : m), null);
  const dominantTenure = (data.tenure_buckets || []).reduce((m, b) => (b.count > (m?.count || 0) ? b : m), null);
  const dominantBand = (data.salary_bands || []).reduce((m, b) => (b.count > (m?.count || 0) ? b : m), null);

  const genderPie = (data.gender || []).map((g) => ({
    name: g.name === "M" ? "Male" : "Female", value: g.value,
  }));

  return (
    <div>
      <PageHeader
        eyebrow="People · Demographics"
        title="The shape of the workforce."
        lede="Gender, age, tenure, salary bands — the makeup of 220 active employees across 25 departments."
      />

      {/* Narrative strip */}
      <div className="grid md:grid-cols-3 gap-px bg-border mb-10">
        <div className="bg-background p-5">
          <div className="label-caps text-primary mb-2">Gender mix</div>
          <div className="font-display text-xl">{malePct}% M · {100 - malePct}% F</div>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            {male} men, {female} women. Track this against industry benchmarks and your floor-facing roles.
          </p>
        </div>
        <div className="bg-background p-5">
          <div className="label-caps mb-2">Age centre</div>
          <div className="font-display text-xl">{youngBucket?.bucket || "—"} · {youngBucket?.count || 0} people</div>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            Largest age cohort. Career-stage signals matter — design L&D, leave and benefit packages around this group first.
          </p>
        </div>
        <div className="bg-background p-5">
          <div className="label-caps mb-2">Tenure & pay shape</div>
          <div className="font-display text-xl">{dominantTenure?.bucket || "—"} · {dominantBand?.band || "—"}</div>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            Most common tenure and salary band. Use this as your anchor for compa-ratio decisions.
          </p>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-10 items-start">
        <PieBlock eyebrow="Gender" title="Gender split" data={genderPie} />
        <PieBlock eyebrow="Type" title="Employee type" data={data.type_split || []} />
        <BarBlock eyebrow="Age" title="Age distribution" data={data.age_buckets || []} xKey="bucket" yKey="count" />
        <BarBlock eyebrow="Tenure" title="Tenure distribution" data={data.tenure_buckets || []} xKey="bucket" yKey="count" />
        <BarBlock eyebrow="Salary" title="Salary bands" data={data.salary_bands || []} xKey="band" yKey="count" />
        <Section eyebrow="Departments" title="Gender × Department">
          <div className="hairline overflow-hidden">
            <table className="w-full table-dense">
              <thead>
                <tr>
                  <th>Department</th>
                  <th className="text-right">Male</th>
                  <th className="text-right">Female</th>
                </tr>
              </thead>
              <tbody>
                {(data.dept_gender || []).map((d) => (
                  <tr key={d.department}>
                    <td className="font-display text-sm">{d.department}</td>
                    <td className="text-right">{d.M}</td>
                    <td className="text-right">{d.F}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
    </div>
  );
}
